import React, { useRef, useEffect, useState } from 'react';

/**
 * SignaturePad — minimal canvas signature capture.
 * Supports mouse + touch (finger/stylus). Exposes clear() and toDataURL().
 * Parent component reads the signature via the onChange callback on pointer-up.
 */
export default function SignaturePad({ onChange, height = 150, testId = 'signature-pad' }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const lastPointRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Handle DPR for crisp strokes
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPointRef.current = getPoint(e);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = getPoint(e);
    const lp = lastPointRef.current;
    if (!lp) { lastPointRef.current = p; return; }
    ctx.beginPath();
    ctx.moveTo(lp.x, lp.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (isEmpty) setIsEmpty(false);
  };

  const end = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
    lastPointRef.current = null;
    if (onChange) {
      const canvas = canvasRef.current;
      onChange(isEmpty ? null : canvas.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="relative rounded-lg border-2 border-dashed border-gray-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: `${height}px`, touchAction: 'none', display: 'block', cursor: 'crosshair' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
          data-testid={`${testId}-canvas`}
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-gray-300 text-sm italic">
            Sign here — draw with your mouse or finger
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>
          {isEmpty ? 'Signature required' : 'Signature captured — you can redraw if you want'}
        </span>
        <button
          type="button"
          onClick={clear}
          className="px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
          data-testid={`${testId}-clear`}
        >
          <i className="fas fa-eraser mr-1"></i>Clear
        </button>
      </div>
    </div>
  );
}
